
import Vue, { PropType } from 'vue';
import { auth, db, Timestamp } from '@/firebase';
import {
	fetchLibraryPage,
	isResolvedNodeDef,
	resolveLibraryScopeAndAccount,
	LIBRARY_PARTIAL_FLAG,
	LibraryFacetFilters,
	LibraryNodeDef,
	LibraryNodeRun,
	LibraryNodeRunResult,
	LibraryRequest,
	LibraryScope,
} from '@/utils/libraryApi';
import { ensureWebClientDeviceId } from '@/utils/webClientDevice';

// The Postgres-backed Library Filter API base (mirrors AuthDevices.vue). The web
// client reuses the caller's Firebase session as a Bearer token.
const LIBRARY_API_BASE_URL: string =
	process.env.VUE_APP_API_BASE_URL || 'https://api2.rundiffusion.com/api/v2';

// The library endpoint returns 403 from several different layers, each with its
// own actionable cause (restServer/api/errors.py ErrorCode). Collapsing them all
// to "you do not have permission" sends people hunting for a permission problem
// when the real cause is an unverified email or the wrong active account.
const LIBRARY_ERROR_MESSAGES: Record<string, string> = {
	ACCOUNT_NOT_MEMBER: 'You are not a member of the selected team account',
	EMAIL_NOT_VERIFIED: 'Verify your email in the web app before filtering team generations',
	ANONYMOUS_NOT_ALLOWED: 'Sign in with your account to filter generations',
	ACCOUNT_NOT_FOUND: 'That team account no longer exists',
	PERMISSION_DENIED: 'You do not have permission to view these generations',
};
import { LibraryRunHydrationMixin } from '@/mixins/LibraryRunHydrationMixin';
import { buildLibraryFilterKey, hasAnyLibraryFilter, LibraryFilterDraft } from '@/utils/libraryFilterFacets';
import _truncate from 'lodash/truncate';
import _range from 'lodash/range';
import _mapKeys from 'lodash/mapKeys';
import _keys from 'lodash/keys';
import _differenceBy from 'lodash/differenceBy';
import _debounce from 'lodash/debounce';
import { mapActions, mapState } from 'vuex';
import { get$bindFirestoreOptions, TeamUserMixin } from '@/mixins';
import { SNACKBAR_STATUS } from '@/constants/constants';
import {
	Avatar,
	Runnit,
	RunnitNode,
	RunnitNodeRun,
	RunnitNodeRunResult,
	RunnitUpload,
	AVATAR_USE_CASE,
	IMAGE_UPLOAD_MODE,
	RUNNIT_NODE_FIELD_TYPE,
	RUNNIT_NODE_RUN_STATE,
	RUNNIT_NODE_STATIC_FIELDS_KEY,
	RUNNIT_NODE_RUN_RESULT_TYPE,
	RunnitNodeDef,
	UPLOAD_FILE_TYPE,
	THUMB_SIZE,
} from '@run-diffusion/shared';
import {
	SETTING_ACTIONS,
	SELECTED_IMAGE_MODE,
	RUNNITS_OWNER_SELECTION,
} from '@/views/Runnits/constants';
import { RunnitState } from '@/store';
import { IMAGE_GALLERY_DIALOG_NAV, ImageGalleryDialogNav } from '@/components/ImageGallery/utils';
import RunnitImage from '@/views/Runnits/RunnitImage.vue';
import RunnitVideo from '@/views/Runnits/RunnitVideo.vue';
import RunnitAsset3D from '@/views/Runnits/RunnitAsset3D.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import ImageInfo from '@/components/ImageGallery/ImageInfo.vue';
import BaseGreyToggleButton from '@/components/base/BaseGreyToggleButton.vue';
import RunnitImageInfoCarouselDialog from '@/views/Runnits/RunnitImageInfoCarouselDialog.vue';
import UploadFullScreenDialog from '@/components/ImageGallery/UploadFullScreenDialog.vue';
import ImageUpload from '@/views/Runnits/RunnitSettings/ImageInput/ImageUpload.vue';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import { ResultFavoritesMixin } from '@/mixins/ResultFavoritesMixin';
import RunnitResult from '@/views/Runnits/RunnitResult.vue';
import ImageModeButton from './ImageModeButton.vue';
import VirtualScrollResults from './VirtualScrollResults.vue';
import SelectedColumnsButton from './SelectedColumnsButton.vue';
import { RUNNIT_LIBRARY_FILTER_LOCATION } from '@/utils';

export default Vue.extend({
	name: 'ImageGallery',
	props: {
		elIdSuffix: { type: String, required: true }, // to prefix html element IDs in the DOM
		height: { type: String, default: '100%' },
		virtualScrollResultsPaddingX: { type: String, default: '16px' },
		showTopSettings: { type: Boolean, default: false },
		insideDrawer: { type: Boolean, default: false },
		isAppDrawerMode: { type: Boolean, default: true },
		insideNode: { type: Boolean, default: false },
		runsGalleryRunnit: { type: Object, default: null },
		runsGalleryNode: { type: Object, default: null },
		navTab: { type: String, default: null },
		loadImmediately: { type: Boolean, default: false },
		isFlat: { type: Boolean, default: false },
		hideToolName: { type: Boolean, default: false },
		backgroundColor: {
			type: String,
			default: null
		},
		availableFilters: {
			type: Array as PropType<string[]>, // ['FEATURED', 'THIS_TOOL', 'TEAM']
			default: () => [],
		},
		// Library pages resolve eligibility across every Team Account via /me.
		// Other hosts omit this and retain the legacy active-Team permission gate.
		teamFilterAllowed: { type: Boolean as PropType<boolean | null>, default: null },
		availableNavTabs: {
			type: Array as PropType<string[]>, // ['GENERATIONS', 'UPLOADS']
			default: () => ['GENERATIONS', 'UPLOADS'],
		},
		toolOnboardingTopCardDismissed: { type: Boolean, default: false },
		filterLocalStorageKey: {
			type: String,
			default: RUNNIT_LIBRARY_FILTER_LOCATION.RUNNITS_LIBRARY,
		},
		aspectRatioFilter: { type: String, default: '' },
		resolutionFilter: { type: String, default: '' },
		toolFilter: { type: String, default: '' },          // nodeDefId
		modelFamilyTagFilter: { type: String, default: '' },
		mediaTagFilter: { type: String, default: '' },
		dateFromFilter: { type: String, default: '' },        // ISO date
		dateToFilter: { type: String, default: '' },          // ISO date
		favoriteFilter: { type: String, default: '' },        // '' | 'favorited'
		ownerFilter: { type: String, default: '' },           // filter-by-user (userId)
		boardFilter: { type: String, default: '' },           // filter-by-board (runnitId)
		scopeFilter: { type: String, default: 'personal' },   // 'personal' | 'team'
		teamIdsFilter: { type: Array as () => string[], default: () => [] }, // teams picked under Team scope
		searchFilter: { type: String, default: '' },          // Library Search term
	},
	mixins: [
		LibraryRunHydrationMixin,
		RunnitsImageSelectMixin,
		TeamUserMixin,
		ResultFavoritesMixin,
	],
	data () {
		return {
			RUNNIT_NODE_RUN_RESULT_TYPE,
			UPLOAD_FILE_TYPE,
			IMAGE_GALLERY_DIALOG_NAV,
			SETTING_ACTIONS,
			SELECTED_IMAGE_MODE,
			RUNNIT_NODE_FIELD_TYPE,
			RUNNIT_NODE_RUN_STATE,
			FINAL_NODE_RUN_STATES: [
				RUNNIT_NODE_RUN_STATE.NSFW,
				RUNNIT_NODE_RUN_STATE.ERROR,
				RUNNIT_NODE_RUN_STATE.DONE,
			],
			PENDING_NODE_RUN_STATES: [
				RUNNIT_NODE_RUN_STATE.INIT,
				RUNNIT_NODE_RUN_STATE.QUEUED,
			],
			IMAGE_UPLOAD_MODE,
			AVATAR_USE_CASE,


			// Cached reversed array to prevent excessive re-renders
			cachedReversedNodeRuns: [],
			lastRealtimeNodeRunsLength: 0,


			imageInfoCarouselConfig: {
				dialogOpen: false,
				nodeRun: null,
				nodeRuns: null,
				nodeRunResult: null,
			},

			uploadFullScreenConfig: {
				dialogOpen: false,
				upload: null as RunnitUpload | null,
			},


			realtimeNodeRunsLoading: false,
			realtimeNodeRuns: [],
			previousRealtimeNodeRunIds: [], // $bind has a problem with immutability, need to keep track of the old IDs to see what needs to go from `realtime` to `more` arrays
			pendingCompletedNodeRunIds: new Set(),
			processedCompletedNodeRunIds: new Set(), // Track node runs that have already been moved to moreNodeRuns
			couldThrowGenerationError: {},
			realtimeNodeRunsLimit: 50, // Reduced from 100 for low-memory devices
			canLoadMoreNodeRuns: false,
			loadingMoreNodeRuns: false,
			// Bumped by refresh(): part of the results grid's key, so the grid remounts and loads again
			refreshNonce: 0,
			moreNodeRuns: [],
			lastMoreNodeRunsDoc: null,
			moreNodeRunsLimit: 24, // Reduced from 48 for low-memory devices

			isInitializedRunnitUploads: false,
			realtimeUploadsEndBeforeSnapshot: null, // cut off cursor between `realtime` & `more` queries
			realtimeUploadsLoading: false,
			realtimeUploads: [],
			oldRealtimeUploadIds: [], // $bind has a problem with immutability, need to keep track of the old IDs to see what needs to go from `realtime` to `more` arrays
			realtimeUploadsLimit: 25, // Reduced from 100 for low-memory devices
			canLoadMoreUploads: true,
			loadingMoreUploads: false,
			moreUploads: [],
			lastMoreUploadsDoc: null,
			moreUploadsLimit: 12, // Reduced from 24 for low-memory devices

			// Avatars
			isInitializedAvatars: false,
			realtimeAvatarsEndBeforeSnapshot: null, // cut off cursor between `realtime` & `more` queries
			realtimeAvatarsLoading: false,
			realtimeAvatars: [],
			oldRealtimeAvatarsIds: [], // $bind has a problem with immutability, need to keep track of the old IDs to see what needs to go from `realtime` to `more` arrays
			realtimeAvatarsLimit: 25, // Reduced from 100 for low-memory devices
			canLoadMoreAvatars: true,
			loadingMoreAvatars: false,
			moreAvatars: [],
			lastMoreAvatarsDoc: null,
			moreAvatarsLimit: 12, // Reduced from 24 for low-memory devices

			currNavTab: null,

			featuredImagePageSize: 8,
			featuredImageFirstPageCount: 0,

			// Debounced methods
			debouncedInitializeRunnitNodeRuns: null,
			debouncedInitializeRunnitUploads: null,
			debouncedInitializeAvatars: null,

			teamGenerationFilters: null as { mediaFilters: Record<string, boolean>, dateRangeFilters: Record<string, string>, toolFilters: Record<string, boolean> } | null,
			nodeDefFetchCache: Object.create(null) as Record<string, Promise<RunnitNodeDef | null>>,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'loadingTeam',
			'runnitState',
			'draftRunnitNodeRun',
			'dragAndDrop',
			'publicRunnitNodeDefsMap',
			'companyRunnitNodeDefsMap',
		]),
		hasActiveDropZones () {
			return this.dragAndDrop && this.dragAndDrop.activeDropZones > 0;
		},
		// True when anything NARROWS the result set (owner/board included;
		// scope/teamIds deliberately not — see NARROWING_LIBRARY_FILTER_KEYS in
		// libraryFilterFacets.ts). This is the "are filters active?" question only.
		// It is NOT the routing question — use isRestLibraryView for that.
		isFilterActive (): boolean {
			return hasAnyLibraryFilter(this.currentLibraryFilterDraft());
		},
		// True when the REST library endpoint owns this view, which is what actually
		// decides routing and realtime suppression.
		//
		// A non-personal scope belongs here even with no narrowing facet: Team and
		// Team results only exist on the REST path. Routing on isFilterActive
		// alone meant selecting Team and a team, with no other facet, silently
		// served the caller's OWN runs from the Firestore feed — the scope selection
		// appeared to do nothing at all.
		isRestLibraryView (): boolean {
			return this.isFilterActive || this.scopeFilter !== 'personal';
		},
		// Changing this string remounts VirtualScrollResults (see :key) so the
		// grid refetches from page 1 whenever any filter — or the scope / the
		// picked team set — changes. Both the field roster (in
		// buildLibraryFilterKey) and the snapshot assembly (in
		// currentLibraryFilterDraft) have a single home, so this can't drift
		// from what isRestLibraryView routes on.
		filterKey (): string {
			return buildLibraryFilterKey(this.currentLibraryFilterDraft());
		},
		imageGalleryNavTabs () {
			const allTabs = [
				{
					id: IMAGE_GALLERY_DIALOG_NAV.GENERATIONS,
					label: 'Your Generations',
					icon: 'mdi-image-multiple-outline',
				},
				{
					id: IMAGE_GALLERY_DIALOG_NAV.UPLOADS,
					label: 'Uploads',
					icon: 'mdi-cloud-upload-outline',
				},
				{
					id: IMAGE_GALLERY_DIALOG_NAV.FAVORITES,
					label: 'Favorites',
					icon: 'mdi-heart-outline',
				},
				// {  // By default this tab isn't used for the runnit workflows so we don't want them visible
				// 	id: IMAGE_GALLERY_DIALOG_NAV.AVATARS,
				// 	label: 'Avatars',
				// 	icon: 'mdi-account-circle-outline',
				// },
			];

			return allTabs.filter(tab => this.availableNavTabs.includes(tab.id));
		},
		navToggleOptions () {
			return this.imageGalleryNavTabs.map((tab) => ({
				value: tab.id,
				label: tab.label,
				icon: tab.icon,
			}));
		},
		reversedRealtimeNodeRuns () {
			return this.isRestLibraryView ? [] : this.cachedReversedNodeRuns;
		},
		showIsQueuingDraftRunnitNodeRunPlaceholderLoaders () {
			return !!(
				this.insideNode &&
				this.draftRunnitNodeRun &&
				this.runnitState.isQueuingDraftRunnitNodeRun &&
				this.runsGalleryRunnit &&
				this.runsGalleryNode &&
				this.runsGalleryNode.id === this.draftRunnitNodeRun.nodeId
			);
		},
		insideDialog () {
			return !!(
				!this.insideDrawer &&
				!this.insideNode &&
				!this.isFlat
			);
		},
		filterOptions () {
			// FAVORITES nav tab pins the filter to FAVORITES (single option → auto-selects, dropdown hides).
			if (this.currNavTab === IMAGE_GALLERY_DIALOG_NAV.FAVORITES) {
				return [{ label: 'Favorites', value: 'FAVORITES', icon: 'mdi-heart-outline' }];
			}
			const defaultOptions = [
				{ label: 'Featured', value: 'FEATURED', icon: 'mdi-star-circle-outline' },
				{ label: 'Library', value: 'ALL', icon: 'mdi-image-multiple-outline' },
				{ label: 'This Tool', value: 'THIS_TOOL', icon: 'mdi-account-circle-outline' },
				{ label: 'Favorites', value: 'FAVORITES', icon: 'mdi-heart-outline' },
				{ label: 'Team', value: 'TEAM', icon: 'mdi-account-multiple-outline' },
			];

			let availableOptions = defaultOptions;

			// Remove featured option if nodeDef doesn't show featured or no featured images
			if (!this.runsGalleryNode?.nodeDef?.showFeatured || !this.hasFeaturedLibraryImages) {
				availableOptions = availableOptions.filter(option => option.value !== 'FEATURED');
			}

			// The Library host can provide a cross-Team eligibility answer. Other
			// gallery contexts keep the existing active-Team behavior.
			const hasTeamOptions = _keys(this.user.teamIds || {}).length > 0;
			const teamAllowed = this.teamFilterAllowed === null
				? hasTeamOptions
					&& this.runnitState.runnitsOwnerSelection !== RUNNITS_OWNER_SELECTION.USER
					&& this.hasManageAllGenerationsPermission
				: this.teamFilterAllowed;
			if (!teamAllowed) {
				availableOptions = availableOptions.filter(option => option.value !== 'TEAM');
			}

			return availableOptions.filter(option => this.availableFilters.includes(option.value));
		},
		hasFeaturedLibraryImages () {
			return this.featuredImageFirstPageCount > 0;
		},
		useFeaturedLibraryAsEmptyState () {
			return this.availableFilters.includes('FEATURED')
				&& this.runsGalleryNode?.nodeDef?.showFeatured
				&& this.hasFeaturedLibraryImages
				&& this.featuredImageFirstPageCount >= this.featuredImagePageSize;
		},
		hasNoImages () {
			return !this.realtimeNodeRunsLoading
				// reversedRealtimeNodeRuns is empty while a filter is active, so this
				// covers both "no live runs" and "live runs suppressed by a filter".
				&& !this.reversedRealtimeNodeRuns.length
				&& !this.loadingMoreNodeRuns
				&& !this.moreNodeRuns.length
				&& !this.useFeaturedLibraryAsEmptyState
				&& !this.showIsQueuingDraftRunnitNodeRunPlaceholderLoaders;
		},
		virtualScrollContentStylesComputed () {
			return {
				...(this.hasNoImages && { paddingBottom: '0' }),
			};
		},
		// The ordered set the full-screen viewer steps through, matching the grid's
		// own order (realtime first, then paged). Images only: the viewer renders a
		// RunnitImage, so stepping onto a video or a 3D asset would show nothing.
		fullScreenViewableUploads (): RunnitUpload[] {
			return [
				...(this.realtimeUploads || []),
				...(this.moreUploads || []),
			].filter((upload: RunnitUpload) => upload && upload.type === UPLOAD_FILE_TYPE.IMG);
		},
	},
	created () {
		// Create debounced methods

		if (!this.debouncedInitializeRunnitNodeRuns) {
			this.debouncedInitializeRunnitNodeRuns = _debounce(this.initializeRunnitNodeRuns, 300);
		}
		if (!this.debouncedInitializeRunnitUploads) {
			this.debouncedInitializeRunnitUploads = _debounce(this.initializeRunnitUploads, 300);
		}
		if (!this.debouncedInitializeAvatars) {
			this.debouncedInitializeAvatars = _debounce(this.initializeAvatars, 300);
		}

		this.initializeRunnitNodeRuns(this.runsGalleryRunnit, this.runsGalleryNode);
	},
	destroyed () {
		// Clear processed node runs tracking
		this.processedCompletedNodeRunIds.clear();
		// Reset previous IDs to prevent stale state on remount
		this.previousRealtimeNodeRunIds = [];

		// Unbind all real-time subscriptions safely
		try {
			if (this.realtimeNodeRuns && this.$firestoreRefs && this.$firestoreRefs.realtimeNodeRuns) {
				this.$unbind('realtimeNodeRuns');
			}
			if (this.realtimeUploads && this.$firestoreRefs && this.$firestoreRefs.realtimeUploads) {
				this.$unbind('realtimeUploads');
			}
			if (this.realtimeAvatars && this.$firestoreRefs && this.$firestoreRefs.realtimeAvatars) {
				this.$unbind('realtimeAvatars');
			}
		} catch (error) {
			console.warn('Error unbinding Firestore subscriptions:', error);
		}
	},
	watch: {
		'runnitState.selectedImageMode' (mode) {
			// Switching INTO info mode with results already loaded must hydrate the
			// list that is now being rendered as detail cards.
			if (mode === SELECTED_IMAGE_MODE.INFO) {
				this.hydratePartialNodeRuns(this.moreNodeRuns);
			}
		},
		filterKey () {
			// TILED resets via its :key remount. INFO renders from moreNodeRuns,
			// so reset + reload it here (covers activating, changing, clearing).
			if (this.runnitState && this.runnitState.selectedImageMode === SELECTED_IMAGE_MODE.INFO) {
				this.resetMoreNodeRuns();
				this.loadMoreNodeRuns(this.runsGalleryRunnit, this.runsGalleryNode);
			}
		},
		loadImmediately: {
			immediate: true,
			async handler (newVal) {
				if (newVal && this.debouncedInitializeRunnitNodeRuns) {
					await this.debouncedInitializeRunnitNodeRuns(this.runsGalleryRunnit, this.runsGalleryNode);
				}
			}
		},
		showIsQueuingDraftRunnitNodeRunPlaceholderLoaders: {
			immediate: true,
			handler (newVal: boolean, oldVal: boolean) {
				if (newVal && newVal !== oldVal && this.draftRunnitNodeRun) {
					const startAtMillis = this.draftRunnitNodeRun.initAt?.toMillis?.() || Date.now();
					this.setDraftLoadingTimerStartAt({
						nodeRunId: this.draftRunnitNodeRun.id,
						startAtMillis,
					});
				}
			},
		},
		navTab: {
			immediate: true,
			handler (newVal, oldVal) {
				if (newVal !== oldVal) {
					this.changeNavTab(newVal);
				}
			}
		},
		currNavTab: {
			immediate: true,
			async handler (newVal: ImageGalleryDialogNav, oldVal: ImageGalleryDialogNav) {
				if (!this.isInitializedRunnitUploads && newVal === IMAGE_GALLERY_DIALOG_NAV.UPLOADS && this.debouncedInitializeRunnitUploads) {
					this.isInitializedRunnitUploads = true;
					await this.debouncedInitializeRunnitUploads();
				}
				if (!this.isInitializedAvatars && newVal === IMAGE_GALLERY_DIALOG_NAV.AVATARS && this.debouncedInitializeAvatars) {
					this.isInitializedAvatars = true;
					await this.debouncedInitializeAvatars();
				}
			},
		},

		runsGalleryRunnit: {
			immediate: true,
			handler: _debounce(async function (newVal: Runnit, oldVal: Runnit) {
				const newId: string = this._get(newVal, 'id') || null;
				const oldId: string = this._get(oldVal, 'id') || null;
				if (
					this.insideNode &&
					newId &&
					newId !== oldId &&
					this.runsGalleryNode &&
					newVal.id === this.runsGalleryNode.runnitId
				) {
					if (this.debouncedInitializeRunnitNodeRuns) {
						await this.debouncedInitializeRunnitNodeRuns(newVal, this.runsGalleryNode);
					}
				}
			}, 500),
		},
		runsGalleryNode: {
			immediate: true,
			async handler (newVal: RunnitNode, oldVal: RunnitNode) {
				const newId: string = this._get(newVal, 'id') || null;
				const oldId: string = this._get(oldVal, 'id') || null;
				if (
					this.insideNode &&
					newId &&
					newId !== oldId &&
					this.runsGalleryRunnit &&
					this.runsGalleryRunnit.id === newVal.runnitId
				) {
					if (this.debouncedInitializeRunnitNodeRuns) {
						await this.debouncedInitializeRunnitNodeRuns(this.runsGalleryRunnit, newVal);
					}
				}
				if (newVal && newVal !== oldVal) {
					await this.initializeFeaturedImagesCount();
				}
			},
		},
		runnitState: {
			immediate: true,
			async handler (newVal: RunnitState, oldVal: RunnitState) {
				if (
					!this.insideNode &&
					newVal &&
					(
						(!this._get(oldVal, 'imageGalleryDrawerOpen') && newVal.imageGalleryDrawerOpen) ||
						(!this._get(oldVal, 'imageGalleryDialogOpen') && newVal.imageGalleryDialogOpen)
					)
				) {
					if (this.debouncedInitializeRunnitNodeRuns) {
						await this.debouncedInitializeRunnitNodeRuns(this.runsGalleryRunnit, this.runsGalleryNode);
					}
				}
				if (newVal.deletedImages.nodeRunResults.length
					&& _differenceBy(newVal?.deletedImages?.nodeRunResults || [], oldVal?.deletedImages?.nodeRunResults || [], 'uuid').length
				) {
					const deletedNodeRunResultsMap: Record<string, RunnitNodeRunResult> = _mapKeys(newVal.deletedImages.nodeRunResults, 'uuid');
					const deletedNsfwPlaceholderNodeRunIds = new Set(
						newVal.deletedImages.nodeRunResults
							.filter((result: RunnitNodeRunResult) => result?.isNsfw && result?.uuid?.startsWith(`placeholder-${result.nodeRunId}-`))
							.map((result: RunnitNodeRunResult) => result.nodeRunId)
					);
					this.moreNodeRuns = this.moreNodeRuns.reduce((acc: RunnitNodeRun[], nodeRun: RunnitNodeRun) => {
						if (deletedNsfwPlaceholderNodeRunIds.has(nodeRun.id)) {
							return acc;
						}

						nodeRun.results = (nodeRun.results || []).filter((nrr: RunnitNodeRunResult) => !deletedNodeRunResultsMap[nrr.uuid]);
						if (
							nodeRun.state !== RUNNIT_NODE_RUN_STATE.DONE ||
							nodeRun.results.length
						) {
							acc.push(nodeRun);
						}
						return acc;
					}, []);
				}
				if (newVal.deletedImages.uploads.length) {
					const deletedUploadsMap: Record<string, RunnitUpload> = _mapKeys(newVal.deletedImages.uploads, 'id');
					this.moreUploads = this.moreUploads.filter((upload: RunnitUpload) => !deletedUploadsMap[upload.id]);
				}
				if (newVal.deletedImages.avatars.length) {
					const deletedAvatarsMap: Record<string, Avatar> = _mapKeys(newVal.deletedImages.avatars, 'id');
					this.moreAvatars = this.moreAvatars.filter((avatar: Avatar) => !deletedAvatarsMap[avatar.id]);
				}
			},
		},
		moreNodeRuns: {
			immediate: true,
			async handler (newVal: RunnitNodeRun[]) {

				// INFO display mode renders an <image-info> card per run, inline. In a
				// filtered view those runs are REST partials with no fields/inputs, so
				// the card needs the same lazy Firestore hydration the carousel does —
				// without it the detail card stays empty while opening the very same
				// result in the dialog shows everything. Gated on INFO (TILED renders
				// no detail cards) and a no-op for non-partials.
				if (this.runnitState?.selectedImageMode === SELECTED_IMAGE_MODE.INFO) {
					this.hydratePartialNodeRuns(newVal);
				}

				let throwGenerationError = false;
				let throwNSFWError = false;
				let errorMessage = null;
				this.couldThrowGenerationError = newVal.reduce((map, val) => {
					if (map[val.id]) {
						map[val.id] = false;
						if (val.state === RUNNIT_NODE_RUN_STATE.ERROR) {
							throwGenerationError = true;
							if (this.$refs.virtualScrollResults) {
								(this.$refs.virtualScrollResults as any).removeNodeRun(val);
							}
						} else if (val.state === RUNNIT_NODE_RUN_STATE.NSFW) {
							throwNSFWError = true;
							errorMessage = 'Failed to pass moderation check';
						}
					}
					return map;
				}, this.couldThrowGenerationError);

				if (throwGenerationError) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Generation Error: ${errorMessage || 'Reach out to our support team if this issue persists'}`,
						show: true,
					});
				} else if (throwNSFWError) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Moderation Error: ${errorMessage || 'Reach out to our support team if this issue persists'}`,
						show: true,
					});
				}
			}
		},
		realtimeNodeRuns: {
			immediate: true,
			handler (newVal: RunnitNodeRun[]) {
				// --- Immediate logic: placeholder addition and timer start ---
				// Update cached reversed array only when content changes
				if (newVal && newVal.length !== this.lastRealtimeNodeRunsLength) {
					this.cachedReversedNodeRuns = [...newVal].reverse();
					this.lastRealtimeNodeRunsLength = newVal.length;
				} else if (!newVal || !newVal.length) {
					this.cachedReversedNodeRuns = [];
					this.lastRealtimeNodeRunsLength = 0;
				}

				if (!this.hasPermissionForNodeRunList(newVal)) {
					return;
				}

				const newNodeRuns = this.getNewNodeRuns(newVal);

				// Filtered views are REST-owned (ADR 0018): no placeholder and no timer
				// for a realtime run that will not render. addPlaceholderRun must still
				// be CALLED while filtered — it self-guards the injection, and its tail
				// is what moves a new generation off the read-only Favorites view.
				// Skipping the call outright strands the user on Favorites.
				if (newNodeRuns.length && this.$refs.virtualScrollResults) {
					const existingPlaceholderIds: string[] = ((this.$refs.virtualScrollResults as any).results || [])
						.filter((r: any) => r && r.isPlaceholder)
						.map((r: any) => r.nodeRunId);
					newNodeRuns.forEach(nodeRun => {
						if (!existingPlaceholderIds.includes(nodeRun.id)) {
							(this.$refs.virtualScrollResults as any).addPlaceholderRun(nodeRun);
						}
						// A suppressed placeholder has nothing to count up, so the timer
						// stays skipped while filtered.
						if (this.isRestLibraryView) return;
						// Set timer start times for new nodeRuns using their initAt or createdAt time
						const initAtMillis = nodeRun.initAt?.toMillis?.();
						const startAtMillis = initAtMillis || Date.now();
						this.setDraftLoadingTimerStartAt({
							nodeRunId: nodeRun.id,
							startAtMillis: startAtMillis,
						});
					});
				}

				// --- Expensive logic: debounced ---
				const completedNodeRunIds = this.getCompletedNodeRunIds(newVal);
				completedNodeRunIds.forEach(id => this.pendingCompletedNodeRunIds.add(id));

				// Update previous IDs immediately to prevent race conditions
				this.previousRealtimeNodeRunIds = (newVal || []).map(({ id }) => id);

				this.handleRealtimeNodeRunsExpensive(newVal);

				// Check for orphaned placeholders that no longer have associated nodeRuns in realtime
				if (this.$refs.virtualScrollResults) {
					const placeholderNodeRunIds = (this.$refs.virtualScrollResults as any).results
						?.filter((result: any) => result.isPlaceholder)
						?.map((result: any) => result.nodeRunId) || [];

					placeholderNodeRunIds.forEach(async (nodeRunId: string) => {
						await this.checkAndFetchCompletedNodeRun(nodeRunId);
					});
				}
			},
		},
		realtimeUploads: {
			immediate: true,
			async handler (newVal: RunnitUpload[]) {

				if (
					newVal &&
					newVal.length &&
					newVal[0].userId !== this.user.id
				) {
					// RunnitUpload list doesn't belong to current User
					return;
				}

				const newRealtimeUploads: Record<string, RunnitUpload> = _mapKeys(newVal || [], 'id');
				const idsRemoved: string[] = this.oldRealtimeUploadIds.filter((id: string) => !newRealtimeUploads[id]);
				if (idsRemoved.length) {
					idsRemoved.forEach(async (id: string) => {
						const uploadRef = db.collection('runnitUploads').doc(id);
						const upload: RunnitUpload = (await uploadRef.get()).data() as RunnitUpload;
						this.moreUploads = [
							{
								...upload,
								get id () { return uploadRef.id; }
							},
							...this.moreUploads,
						];
					});
				}

				this.oldRealtimeUploadIds = (newVal || []).map(({ id }) => id);
			},
		},
		realtimeAvatars: {
			immediate: true,
			async handler (newVal: Avatar[]) {

				if (
					newVal &&
					newVal.length &&
					newVal[0].userId !== this.user.id
				) {
					// Avatar list doesn't belong to current User
					return;
				}

				const newRealtimeAvatars: Record<string, Avatar> = _mapKeys(newVal || [], 'id');
				const idsRemoved: string[] = this.oldRealtimeAvatarsIds.filter((id: string) => !newRealtimeAvatars[id]);
				if (idsRemoved.length) {
					idsRemoved.forEach(async (id: string) => {
						const avatarRef = db.collection('avatars').doc(id);
						const avatar: Avatar = (await avatarRef.get()).data() as Avatar;
						this.moreAvatars = [
							{
								...avatar,
								get id () { return avatarRef.id; }
							},
							...this.moreAvatars,
						];
					});
				}

				this.oldRealtimeAvatarsIds = (newVal || []).map(({ id }) => id);
			},
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'updateSnackbar',
			'setDraftLoadingTimerStartAt',
			'removeDraftLoadingTimer',
		]),
		_truncate,
		getEnqueueResultsPlaceholder (nodeRun: RunnitNodeRun) {
			const length: number = this._get(nodeRun, `staticInputs[${RUNNIT_NODE_STATIC_FIELDS_KEY.numResults}]`) || 1;
			return _range(length);
		},
		getNsfwPlaceholderResult (nodeRun: RunnitNodeRun): RunnitNodeRunResult {
			return {
				uuid: `placeholder-${nodeRun.id}-0`,
				nodeRunId: nodeRun.id,
				type: RUNNIT_NODE_RUN_RESULT_TYPE.IMG,
				isNsfw: true,
				file: {
					width: THUMB_SIZE,
					height: THUMB_SIZE,
				},
			} as RunnitNodeRunResult;
		},
		async checkAndFetchCompletedNodeRun (nodeRunId: string) {
			if (this.isRestLibraryView) return;

			// Check if this nodeRun is no longer in realtime but we still have placeholders for it
			const isInRealtime = this.realtimeNodeRuns.some(nr => nr.id === nodeRunId);
			const isInMoreNodeRuns = this.moreNodeRuns.some(nr => nr.id === nodeRunId);

			if (!isInRealtime && !isInMoreNodeRuns && !this.processedCompletedNodeRunIds.has(nodeRunId)) {
				try {
					// Fetch the completed nodeRun from the database
					const nodeRunRef = db.collection('runnitNodeRuns').doc(nodeRunId);
					const nodeRun: RunnitNodeRun = (await nodeRunRef.get()).data() as RunnitNodeRun;

					if (nodeRun && (nodeRun.state === RUNNIT_NODE_RUN_STATE.DONE || nodeRun.results?.length)) {
						const nonDeletedResults = (nodeRun.results || []).filter((result: RunnitNodeRunResult) => !result.deletedAt);
						let fetchedNodeDef = null;

						if (!isResolvedNodeDef(nodeRun.nodeDef)) {
							try {
								const nodeDefRef = db.doc(`runnitNodeDefs/${nodeRun.nodeDefId}`);
								const nodeDef: RunnitNodeDef = (await nodeDefRef.get()).data() as RunnitNodeDef;
								fetchedNodeDef = { ...nodeDef, get id () { return nodeRun.nodeDefId } }
							} catch (err) {
								console.error('Failed to fetch the generating tool', err);
							}
						}

						const updatedNodeRun = {
							...nodeRun,
							results: nonDeletedResults,
							get id () { return nodeRunRef.id; },
							...(fetchedNodeDef ? { nodeDef: fetchedNodeDef } : {}),
						};

						// Add to moreNodeRuns and update VirtualScrollResults
						this.moreNodeRuns = [
							updatedNodeRun,
							...this.moreNodeRuns,
						];

						if (this.$refs.virtualScrollResults) {
							(this.$refs.virtualScrollResults as any).addNewRun(updatedNodeRun);
						}

						// Mark as processed to prevent duplicate fetches
						this.processedCompletedNodeRunIds.add(nodeRunId);
						this.removeDraftLoadingTimer(nodeRunId);
					}
				} catch (err) {
					console.error('Failed to fetch completed nodeRun:', err);
				}
			}
		},
		async loadMoreFilterNodeRuns () {
			try {
				this.loadingMoreNodeRuns = true;
				// INFO mode on the library page: ALL→personal scope.
				const { results, hasMore, lastDoc } = await this.fetchFilterPage(
					this.lastMoreNodeRunsDoc, this.moreNodeRunsLimit, 'ALL',
				);
				this.moreNodeRuns = [...this.moreNodeRuns, ...results];
				this.canLoadMoreNodeRuns = hasMore;
				this.lastMoreNodeRunsDoc = lastDoc;
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error filtering generations',
					show: true,
				});
			} finally {
				this.loadingMoreNodeRuns = false;
			}
		},
		async loadMoreNodeRuns (runnit: Runnit, node: RunnitNode) {
			// Track whether this call is the initial page so the parent (e.g.
			// RunnitWorkflow's count chip) can react to the first results
			// landing for this node, without us double-emitting on subsequent
			// "load more" pages.
			const wasFirstPage: boolean = !this.lastMoreNodeRunsDoc;
			if (this.isRestLibraryView && this.currNavTab !== IMAGE_GALLERY_DIALOG_NAV.FAVORITES) {
				return this.loadMoreFilterNodeRuns();
			}
			try {
				this.loadingMoreNodeRuns = true;

				let nodeRunsRef = db.collection('runnitNodeRuns')
					.where('state', 'in', this.FINAL_NODE_RUN_STATES)
					.where('deletedAt', '==', null)
					/*
					- Don't order by createdAt, because drafts are created before the run is fired off
					- Order by Descending to show most recent at the top
					 */
					.orderBy('initAt', 'desc')
					.limit(this.moreNodeRunsLimit);

				if (runnit && node) {
					// index created: runnitNodeRuns: deletedAt Ascending nodeId Ascending runnitId Ascending state Ascending initAt Descending __name__ Descending
					nodeRunsRef = nodeRunsRef
						.where('nodeId', '==', node.id)
						.where('runnitId', '==', runnit.id);
				} else {
					// index created: runnitNodeRuns: deletedAt Ascending state Ascending userId Ascending initAt Descending __name__ Descending
					nodeRunsRef = nodeRunsRef
						.where('userId', '==', this.user.id);

					// Filter out template clones in library view
					const isLibraryView = !runnit || !node;
					if (isLibraryView) {
						nodeRunsRef = nodeRunsRef.where('templateOriginalData', '==', null);
					}
				}

				let nodeRunsSnapshot;
				if (this.lastMoreNodeRunsDoc) {
					nodeRunsSnapshot = await nodeRunsRef.startAfter(this.lastMoreNodeRunsDoc).get();
				} else {
					nodeRunsSnapshot = await nodeRunsRef.get();
				}

				if (nodeRunsSnapshot.empty) {
					this.canLoadMoreNodeRuns = false;
					this.lastMoreNodeRunsDoc = null;
				} else {
					this.canLoadMoreNodeRuns = nodeRunsSnapshot.docs.length === this.moreNodeRunsLimit;
					this.lastMoreNodeRunsDoc = nodeRunsSnapshot.docs[nodeRunsSnapshot.docs.length - 1];

					let filteredMoreNodeRuns: RunnitNodeRun[] = await Promise.all(nodeRunsSnapshot.docs.map(async (doc) => {
						const nodeRun: RunnitNodeRun = doc.data() as RunnitNodeRun;

						let fetchedNodeDef = null;

						if (!isResolvedNodeDef(nodeRun.nodeDef)) {
							try {
								const nodeDefRef = db.doc(`runnitNodeDefs/${doc.data().nodeDefId}`);
								const nodeDef: RunnitNodeDef = (await nodeDefRef.get()).data() as RunnitNodeDef;
								fetchedNodeDef = { ...nodeDef, id: doc.data().nodeDefId };
							} catch (err) {
								console.error('Failed to fetch the generating tool', err);
							}
						}

						const filteredResults = (nodeRun.results || []).filter((result: RunnitNodeRunResult) => !result.deletedAt);
						if (nodeRun.state !== RUNNIT_NODE_RUN_STATE.DONE || filteredResults.length) {
							return {
								...nodeRun,
								results: filteredResults,
								get id () { return doc.id; },
								...(fetchedNodeDef ? { nodeDef: fetchedNodeDef } : {}),
							};
						}
						return null;
					}));
					filteredMoreNodeRuns = filteredMoreNodeRuns.filter((nodeRun) => nodeRun) as RunnitNodeRun[];

					this.moreNodeRuns = [
						...this.moreNodeRuns,
						...filteredMoreNodeRuns,
					];
				}

				if (wasFirstPage) {
					// Surface the initial page result to parents (e.g. the
					// RunnitWorkflow count chip). `count` is the number of docs
					// in the first page (0..moreNodeRunsLimit); `hasMore` is
					// true iff the page filled the limit, suggesting >= limit
					// total. No extra Firestore reads — this is just signal off
					// the query we already ran.
					this.$emit('first-page-loaded', {
						nodeId: node?.id || null,
						count: this.moreNodeRuns.length,
						hasMore: this.canLoadMoreNodeRuns,
					});
				}
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error loading more generations',
					show: true,
				});
			} finally {
				this.loadingMoreNodeRuns = false;
			}
		},
		/**
		 * Load the results again from the first page, the way a filter change does:
		 * the tiled grid remounts (see refreshNonce in its key), and the info list
		 * resets and loads. For the Library's Refresh button: a filtered or team view
		 * reads a copy of the data that trails new runs by a few seconds.
		 */
		refresh () {
			this.refreshNonce++;
			if (this.runnitState && this.runnitState.selectedImageMode === SELECTED_IMAGE_MODE.INFO) {
				this.resetMoreNodeRuns();
				this.loadMoreNodeRuns(this.runsGalleryRunnit, this.runsGalleryNode);
			}
		},
		resetMoreNodeRuns () {
			this.canLoadMoreNodeRuns = false;
			this.moreNodeRuns = [];
			this.lastMoreNodeRunsDoc = null;
			this.processedCompletedNodeRunIds.clear(); // Clear processed node runs when resetting
			this.previousRealtimeNodeRunIds = []; // Reset previous IDs when resetting
		},
		async initializeRunnitNodeRuns (runnit: Runnit, node: RunnitNode) {
			if (this.realtimeNodeRunsLoading) {
				return;
			}
			this.realtimeNodeRunsLoading = true;
			try {
				// Check if component is still mounted before proceeding
				if (!this.$firestoreRefs) {
					return;
				}
				if (this.insideNode && !node) {
					return;
				}

				this.resetMoreNodeRuns();
				this.loadMoreNodeRuns(runnit, node); // Don't await because both queries are already separated by different `state` where filters
				// Unbind existing subscription to prevent memory leaks
				if (this.realtimeNodeRuns && this.realtimeNodeRuns.length && this.$firestoreRefs && this.$firestoreRefs.realtimeNodeRuns) {
					this.$unbind('realtimeNodeRuns');
				}
				let nodeRunsRef = db.collection('runnitNodeRuns')
					.where('state', 'in', this.PENDING_NODE_RUN_STATES)
					.where('deletedAt', '==', null)
					/*
					- Don't order by createdAt, because drafts are created before the run is fired off
					- Order by Ascending because we're limiting the # of docs & then we will reverse on the frontend
					 */
					.orderBy('initAt', 'asc')
					.limit(this.realtimeNodeRunsLimit);
				if (runnit && node) {
					// index created: runnitNodeRuns: deletedAt Ascending nodeId Ascending runnitId Ascending state Ascending initAt Ascending __name__ Ascending
					nodeRunsRef = nodeRunsRef
						.where('nodeId', '==', node.id)
						.where('runnitId', '==', runnit.id);
				} else {
					// index created: runnitNodeRuns: deletedAt Ascending state Ascending userId Ascending initAt Ascending __name__ Ascending
					nodeRunsRef = nodeRunsRef
						.where('userId', '==', this.user.id);
				}
				// Check again before binding to ensure component is still mounted
				if (!this.$firestoreRefs) {
					return;
				}
				await this.$bind(
					'realtimeNodeRuns',
					nodeRunsRef,
					get$bindFirestoreOptions(),
				);
				// After binding, ensure placeholders are present for current realtime node runs
				this.$nextTick(() => {
					if (!this.$refs.virtualScrollResults || !this.realtimeNodeRuns?.length) return;
					const existingPlaceholderIds: string[] = ((this.$refs.virtualScrollResults as any).results || [])
						.filter((r: any) => r && r.isPlaceholder)
						.map((r: any) => r.nodeRunId);
					this.realtimeNodeRuns.forEach((nodeRun: any) => {
						const shouldShow = this.insideNode
							? nodeRun.nodeId === this.runsGalleryNode?.id
							: nodeRun.userId === this.user?.id;
						if (shouldShow && !this.processedCompletedNodeRunIds.has(nodeRun.id) && !existingPlaceholderIds.includes(nodeRun.id)) {
							(this.$refs.virtualScrollResults as any).addPlaceholderRun(nodeRun);
						}
					});
				});
			} catch (e) {
				console.error(e);
				// Only show error if component is still mounted
				if (this.$firestoreRefs) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: 'Error loading generations',
						show: true,
					});
				}
			} finally {
				// Only update loading state if component is still mounted
				if (this.$firestoreRefs) {
					this.realtimeNodeRunsLoading = false;
				}
			}
		},
		async loadMoreUploads () {
			try {
				this.loadingMoreUploads = true;

				// index created: runnitUploads: deletedAt Ascending userId Ascending createdAt Descending __name__ Descending
				const uploadsRef = db.collection('runnitUploads')
					.where('deletedAt', '==', null)
					.where('userId', '==', this.user.id)
					.orderBy('createdAt', 'desc')
					.limit(this.moreUploadsLimit);

				let uploadsSnapshot;
				if (this.lastMoreUploadsDoc) {
					uploadsSnapshot = await uploadsRef.startAfter(this.lastMoreUploadsDoc).get();
				} else {
					uploadsSnapshot = await uploadsRef.get();
				}

				if (uploadsSnapshot.empty) {
					this.canLoadMoreUploads = false;
					this.lastMoreUploadsDoc = null;
				} else {
					this.canLoadMoreUploads = uploadsSnapshot.docs.length === this.moreUploadsLimit;
					this.lastMoreUploadsDoc = uploadsSnapshot.docs[uploadsSnapshot.docs.length - 1];

					// Mark the end of the `realtime` array here
					if (!this.moreUploads.length) {
						this.realtimeUploadsEndBeforeSnapshot = uploadsSnapshot.docs[0];
					}

					this.moreUploads = [
						...this.moreUploads,
						...uploadsSnapshot.docs.map(doc => ({
							...doc.data(),
							get id () { return doc.id },
						})),
					];
				}
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error loading more uploads',
					show: true,
				});
			} finally {
				this.loadingMoreUploads = false;
			}
		},
		resetMoreUploads () {
			this.canLoadMoreUploads = false;
			this.moreUploads = [];
			this.lastMoreUploadsDoc = null;
			this.realtimeUploadsEndBeforeSnapshot = null;
		},
		async initializeRunnitUploads () {

			try {
				// Check if component is still mounted before proceeding
				if (!this.$firestoreRefs) {
					return;
				}

				this.resetMoreUploads();
				await this.loadMoreUploads();
				this.realtimeUploadsLoading = true;

				// Unbind existing subscription to prevent memory leaks
				if (this.realtimeUploads && this.realtimeUploads.length && this.$firestoreRefs && this.$firestoreRefs.realtimeUploads) {
					this.$unbind('realtimeUploads');
				}

				// index created: runnitUploads: deletedAt Ascending userId Ascending createdAt Descending __name__ Descending
				let uploadsRef = db.collection('runnitUploads')
					.where('deletedAt', '==', null)
					.where('userId', '==', this.user.id)
					.orderBy('createdAt', 'desc')
					.limit(this.realtimeUploadsLimit);

				if (this.realtimeUploadsEndBeforeSnapshot) {
					uploadsRef = uploadsRef.endBefore(this.realtimeUploadsEndBeforeSnapshot);
				}

				// Check again before binding to ensure component is still mounted
				if (!this.$firestoreRefs) {
					return;
				}

				await this.$bind(
					'realtimeUploads',
					uploadsRef,
					get$bindFirestoreOptions(),
				);
			} catch (e) {
				console.error(e);
				// Only show error if component is still mounted
				if (this.$firestoreRefs) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: 'Error loading uploads',
						show: true,
					});
				}
			} finally {
				// Only update loading state if component is still mounted
				if (this.$firestoreRefs) {
					this.realtimeUploadsLoading = false;
				}
			}
		},
		async loadMoreAvatars () {
			try {
				this.loadingMoreAvatars = true;

				// index created: avatars: deletedAt Ascending useCase Ascending userId Ascending createdAt Descending __name__ Descending
				const avatarsRef = db.collection('avatars')
					.where('deletedAt', '==', null)
					.where('userId', '==', this.user.id)
					.where('useCase', '==', 'RUNNIT')
					.orderBy('createdAt', 'desc')
					.limit(this.moreAvatarsLimit);

				let avatarsSnapshot;
				if (this.lastMoreAvatarsDoc) {
					avatarsSnapshot = await avatarsRef.startAfter(this.lastMoreAvatarsDoc).get();
				} else {
					avatarsSnapshot = await avatarsRef.get();
				}

				if (avatarsSnapshot.empty) {
					this.canLoadMoreAvatars = false;
					this.lastMoreAvatarsDoc = null;
				} else {
					this.canLoadMoreAvatars = avatarsSnapshot.docs.length === this.moreAvatarsLimit;
					this.lastMoreAvatarsDoc = avatarsSnapshot.docs[avatarsSnapshot.docs.length - 1];

					// Mark the end of the `realtime` array here
					if (!this.moreAvatars.length) {
						this.realtimeAvatarsEndBeforeSnapshot = avatarsSnapshot.docs[0];
					}

					this.moreAvatars = [
						...this.moreAvatars,
						...avatarsSnapshot.docs.map(doc => ({
							...doc.data(),
							get id () { return doc.id },
						})),
					];
				}
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error loading more avatars',
					show: true,
				});
			} finally {
				this.loadingMoreAvatars = false;
			}
		},
		resetMoreAvatars () {
			this.canLoadMoreAvatars = false;
			this.moreAvatars = [];
			this.lastMoreAvatarsDoc = null;
			this.realtimeAvatarsEndBeforeSnapshot = null;
		},
		async initializeAvatars () {

			try {
				// Check if component is still mounted before proceeding
				if (!this.$firestoreRefs) {
					return;
				}

				this.resetMoreAvatars();
				await this.loadMoreAvatars();
				this.realtimeAvatarsLoading = true;

				// Unbind existing subscription to prevent memory leaks
				if (this.realtimeAvatars && this.realtimeAvatars.length && this.$firestoreRefs && this.$firestoreRefs.realtimeAvatars) {
					this.$unbind('realtimeAvatars');
				}

				// index created: avatars: deletedAt Ascending useCase Ascending userId Ascending createdAt Descending __name__ Descending
				let avatarsRef = db.collection('avatars')
					.where('deletedAt', '==', null)
					.where('userId', '==', this.user.id)
					.where('useCase', '==', 'RUNNIT')
					.orderBy('createdAt', 'desc')
					.limit(this.realtimeAvatarsLimit);

				if (this.realtimeAvatarsEndBeforeSnapshot) {
					avatarsRef = avatarsRef.endBefore(this.realtimeAvatarsEndBeforeSnapshot);
				}

				// Check again before binding to ensure component is still mounted
				if (!this.$firestoreRefs) {
					return;
				}

				await this.$bind(
					'realtimeAvatars',
					avatarsRef,
					get$bindFirestoreOptions(),
				);
			} catch (e) {
				console.error(e);
				// Only show error if component is still mounted
				if (this.$firestoreRefs) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: 'Error loading avatars',
						show: true,
					});
				}
			} finally {
				// Only update loading state if component is still mounted
				if (this.$firestoreRefs) {
					this.realtimeAvatarsLoading = false;
				}
			}
		},
		onNodeRunsContainerScrollTop () {
			this.$scrollTo(`#image-gallery-node-runs-anchor-${this.elIdSuffix}`, 800, {
				container: `#image-gallery-node-runs-container-${this.elIdSuffix}`,
			});
		},
		onUploadsContainerScrollTop () {
			this.$scrollTo(`#image-gallery-uploads-anchor-${this.elIdSuffix}`, 800, {
				container: `#image-gallery-uploads-container-${this.elIdSuffix}`,
			});
		},
		onAvatarsContainerScrollTop () {
			this.$scrollTo(`#image-gallery-avatars-anchor-${this.elIdSuffix}`, 800, {
				container: `#image-gallery-avatars-container-${this.elIdSuffix}`,
			});
		},
		onClose () {
			this.$emit('close');
		},
		determineWidth () {
			let width = '100%';

			if (
				this.$vuetify.breakpoint.smAndUp &&
				!this.insideDrawer
			) {
				width = '50%';
			}

			return width;
		},
		// RunnitImage only emits `on-click` outside selection mode, so a picker in
		// selection mode still selects rather than opening the viewer.
		onUploadClick (upload: RunnitUpload) {
			this.uploadFullScreenConfig = {
				dialogOpen: !!upload,
				upload,
			};
		},
		onNodeRunResultClick (nodeRun: RunnitNodeRun, nodeRunResult: RunnitNodeRunResult) {
			this.imageInfoCarouselConfig = {
				dialogOpen: !!(nodeRun && nodeRunResult),
				nodeRun,
				nodeRunResult,
			};
		},
		changeNavTab (tabId: string) {
			this.currNavTab = tabId;
			this.$emit('on-nav-tab-change', tabId);
		},
		handleOnNodeRunResultSingleSelection (nodeRun: RunnitNodeRun, nodeRunResult: RunnitNodeRunResult) {
			this.onNodeRunResultSingleSelection(nodeRun, nodeRunResult);
			this.$emit('selection-chosen', nodeRunResult);
		},
		// The virtual scroller owns the displayed result list and its REST cursor.
		// Let it collect every remaining page, then publish one selection-state
		// update so a failed or stale drain can never leave a partial selection.
		async selectAllShownResults () {
			const selectionState = this.currentSelectionState;
			if (!selectionState || !selectionState.nodeRunResultSelectionMode) {
				return { status: 'stale' as const };
			}

			const virtualScrollResults = this.$refs.virtualScrollResults as any;
			if (!virtualScrollResults || typeof virtualScrollResults.collectAllSelectableResults !== 'function') {
				return { status: 'failed' as const };
			}
			const outcome = await virtualScrollResults.collectAllSelectableResults(
				selectionState.canSelectTypes || [],
				selectionState.triggeringId,
			);
			if (outcome.status !== 'success') return outcome;

			const currentSelectionState = this.currentSelectionState;
			if (
				!currentSelectionState ||
				currentSelectionState.triggeringId !== selectionState.triggeringId ||
				!currentSelectionState.nodeRunResultSelectionMode
			) return { status: 'stale' as const };

			this.updateSelectionState({
				...currentSelectionState,
				selectedNodeRunsMap: outcome.selectedNodeRunsMap,
				selectedNodeRunResults: outcome.selectedNodeRunResults,
				selectedLoadedAssets: [],
				selectedUploads: [],
				selectedAvatars: [],
			});
			// `truncated` means the ceiling stopped this short of everything the
			// filter matches. The selection is complete and actionable either way;
			// the host decides how to say so.
			return {
				status: 'success' as const,
				truncated: !!outcome.truncated,
				selectedCount: outcome.selectedNodeRunResults.length,
			};
		},
		cancelSelectAllShownResults (): void {
			const virtualScrollResults = this.$refs.virtualScrollResults as any;
			if (virtualScrollResults && typeof virtualScrollResults.cancelSelectAllResults === 'function') {
				virtualScrollResults.cancelSelectAllResults();
			}
		},
		handleOnUploadSingleSelection (upload: RunnitUpload) {
			this.onUploadSingleSelection(upload);
			this.$emit('selection-chosen', upload);
		},
		handleOnAvatarSingleSelection (avatar: Avatar) {
			this.onAvatarSingleSelection(avatar);
			this.$emit('selection-chosen', avatar);
		},
		handleIsPublicUpdated (payload: { nodeRunResult: RunnitNodeRunResult, nodeRun: RunnitNodeRun }) {
			this.imageInfoCarouselConfig.nodeRun = { ...payload.nodeRun };
			this.imageInfoCarouselConfig.nodeRunResult = { ...payload.nodeRunResult };
		},
		onNodeRunsInfiniteScroll (entries) {
			if (entries.length && entries[0].isIntersecting) {
				this.loadMoreNodeRuns(this.runsGalleryRunnit, this.runsGalleryNode);
			}
		},
		onUploadsInfiniteScroll (entries) {
			if (entries.length && entries[0].isIntersecting) {
				this.loadMoreUploads();
			}
		},
		onAvatarsInfiniteScroll (entries) {
			if (entries.length && entries[0].isIntersecting) {
				this.loadMoreAvatars();
			}
		},
		async fetchCachedNodeDefForNodeRun (nodeRun: RunnitNodeRun): Promise<RunnitNodeDef | null> {
			if (!nodeRun?.nodeDefId || !nodeRun.nodeDef) return null;
			if (nodeRun.nodeDef && typeof nodeRun.nodeDef === 'object' && nodeRun.nodeDef.title) return null;

			const nodeDefId = nodeRun.nodeDefId;
			if (!this.nodeDefFetchCache[nodeDefId]) {
				this.nodeDefFetchCache[nodeDefId] = db.doc(`runnitNodeDefs/${nodeDefId}`).get()
					.then((nodeDefSnapshot) => {
						const nodeDef = nodeDefSnapshot.data() as RunnitNodeDef | undefined;
						if (!nodeDef) return null;
						return Object.freeze({
							...nodeDef,
							id: nodeDefId,
						}) as RunnitNodeDef;
					})
					.catch((err) => {
						delete this.nodeDefFetchCache[nodeDefId];
						console.error('Failed to fetch the generating tool', err);
						return null;
					});
			}

			return this.nodeDefFetchCache[nodeDefId];
		},
		currentLibraryFacetFilters (): LibraryFacetFilters {
			return {
				aspectRatioFilter: this.aspectRatioFilter,
				resolutionFilter: this.resolutionFilter,
				toolFilter: this.toolFilter,
				modelFamilyTagFilter: this.modelFamilyTagFilter,
				mediaTagFilter: this.mediaTagFilter,
				dateFromFilter: this.dateFromFilter,
				dateToFilter: this.dateToFilter,
				favoriteFilter: this.favoriteFilter,
				searchFilter: this.searchFilter,
			};
		},
		// The full applied-filter snapshot (facets + owner/board + scope/team set)
		// in LibraryFilterDraft shape — the single assembly point consumed by both
		// isFilterActive and filterKey. fetchFilterPage stays separate on purpose:
		// it builds the differently-shaped LibraryRequest wire object.
		currentLibraryFilterDraft (): Partial<LibraryFilterDraft> {
			return {
				...this.currentLibraryFacetFilters(),
				ownerFilter: this.ownerFilter,
				boardFilter: this.boardFilter,
				scopeFilter: this.scopeFilter as LibraryScope,
				teamIdsFilter: this.teamIdsFilter,
			};
		},
		// Resolve the account selection for the REST Library request. Pure logic
		// lives in resolveLibraryScopeAndAccount (libraryApi.ts) so request
		// construction is unit-testable without mounting this component.
		resolveLibraryScope (selectedFilter: string | null): { scope: 'mine' | 'all'; teamId: string | null; blocked?: boolean } {
			return resolveLibraryScopeAndAccount({
				panelScope: this.scopeFilter as LibraryScope,
				selectedFilter,
				activeTeamId: (this.team && this.team.id) || '',
			});
		},
		async getLibraryIdToken (): Promise<string | null> {
			const user = auth.currentUser;
			if (!user) return null;
			try {
				return await user.getIdToken();
			} catch (e) {
				console.error('Library: failed to acquire id token', e);
				return null;
			}
		},
		// Map one REST `node_run` object to the Firestore-shaped RunnitNodeRun the
		// VirtualScrollResults renderer consumes: attach the nodeDef from the
		// response's tools map (no Firestore re-fetch), drop result-level
		// soft-deletes, stamp nodeRunId on each result (the renderer keys
		// runnitNodeRunsById by it), and restore a Timestamp initAt.
		mapLibraryNodeRun (
			run: LibraryNodeRun,
			nodeDefs: Record<string, LibraryNodeDef>,
		): RunnitNodeRun | null {
			const filteredResults = (run.results || [])
				.filter((r: LibraryNodeRunResult) => r && !r.deletedAt)
				.map((r: LibraryNodeRunResult) => ({ ...r, nodeRunId: r.nodeRunId || run.id }));
			if (!filteredResults.length) return null;
			const initMs: number = run.init_at ? Date.parse(run.init_at) : NaN;
			const initAt = Number.isNaN(initMs) ? null : Timestamp.fromMillis(initMs);
			const nodeDef = (run.tool_id && nodeDefs && nodeDefs[run.tool_id]) || null;
			// Partial renderer view-model assembled from REST data — the gallery reads
			// only a subset of RunnitNodeRun (id, nodeDefId, nodeDef, results, initAt),
			// so we assert the shape rather than reconstruct every field.
			return {
				id: run.id,
				[LIBRARY_PARTIAL_FLAG]: true,
				nodeDefId: run.tool_id,
				userId: run.user_id,
				teamId: run.team_id,
				// Synced columns, so they cost nothing extra on the wire. email
				// drives the ImageInfo attribution without waiting on hydration;
				// runnitId/nodeId are REQUIRED by the resultFavorites security rule,
				// which compares them against the source run doc
				// (src.nodeId == data.nodeId), so favoriting from a filtered view is
				// rejected without them. Null, never undefined — Firestore rejects
				// undefined values outright.
				email: run.email || null,
				runnitId: run.board_id || null,
				nodeId: run.node_id || null,
				state: RUNNIT_NODE_RUN_STATE.DONE,
				initAt,
				results: filteredResults,
				...(nodeDef ? { nodeDef } : {}),
			} as unknown as RunnitNodeRun;
		},
		// Postgres-backed filtered page (replaces the Firestore-direct path). The
		// opaque REST cursor flows back through `lastDoc` unchanged. Request/response
		// (no realtime) for the filtered view — accepted (ADR 0018).
		async fetchFilterPage (lastDoc, pageSize: number, selectedFilter: string | null) {
			const { scope, teamId, blocked } = this.resolveLibraryScope(selectedFilter);
			// Path A: a team read with no active team cannot succeed (the
			// server denies a PersonalAccount request for a team scope). Tell the user
			// instead of firing a request that is guaranteed to 403 and then rendering
			// the denial as an empty grid.
			if (blocked) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Switch to a team account to filter team generations',
					show: true,
					timeout: 6000,
				});
				return { results: [], hasMore: false, lastDoc: null, failed: true };
			}
			const idToken = await this.getLibraryIdToken();
			if (!idToken) return { results: [], hasMore: false, lastDoc: null, failed: true };

			const isFavorites = selectedFilter === 'FAVORITES';
			// Favorites per-node scope (board context) mirrors the old THIS_TOOL scope:
			// per-node when a node is in context, all favorites otherwise.
			const perNode = isFavorites && !!(this.runsGalleryRunnit && this.runsGalleryNode);
			const request: LibraryRequest = {
				scope,
				limit: pageSize,
				cursor: (typeof lastDoc === 'string') ? lastDoc : null,
				filters: this.currentLibraryFacetFilters(),
				userId: this.ownerFilter || null,
				// A team read names its teams in team_id: whichever the panel
				// picked, else the team being acted as. Personal sends none.
				teamIds: scope === 'all'
					? ((this.teamIdsFilter && this.teamIdsFilter.length) ? this.teamIdsFilter : [teamId as string])
					: null,
				favorites: isFavorites,
				nodeId: perNode ? this.runsGalleryNode.id : null,
				// Board filter (from the panel) wins; else the favorites per-node
				// board context. Both narrow to one board via request.runnitId.
				runnitId: this.boardFilter || (perNode ? this.runsGalleryRunnit.id : null),
			};

			// The API picks the response shape from the calling client. This browser
			// must present its web_app device id to be served node_run, which is the
			// shape everything below parses. Registration is best-effort and cached
			// in localStorage, so this is a no-op after the first successful load;
			// if it fails we still request, and handle the wrong shape below.
			const deviceId = await ensureWebClientDeviceId({
				baseUrl: LIBRARY_API_BASE_URL,
				idToken,
			});

			const result = await fetchLibraryPage({
				baseUrl: LIBRARY_API_BASE_URL,
				idToken,
				deviceId,
				request,
			});
			if (!result.ok) {
				// Still a graceful empty page (never wedge the gallery), but no longer a
				// SILENT one: a permission denial used to be indistinguishable from "no
				// results", which is what made the team-scope 403 read as a filter bug.
				const reason: string = (result as { reason: string }).reason;
				const serverCode: string | null = (result as { code?: string }).code || null;
				const serverMessage: string | null = (result as { message?: string }).message || null;
				console.error(`Library fetch failed: ${reason}`, { serverCode, serverMessage });
				// A 403 is raised at several layers with different causes, so map the
				// server's error code to something the user can act on rather than a
				// single catch-all "no permission".
				const message: string = LIBRARY_ERROR_MESSAGES[serverCode || ''] || (
					reason === 'forbidden'
						? 'You do not have permission to view these generations'
						: reason === 'unauthorized'
							? 'Your session expired, please sign in again'
							: 'Could not load filtered generations, please try again'
				);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message,
					show: true,
					timeout: 6000,
				});
				return { results: [], hasMore: false, lastDoc: null, failed: true };
			}

			const nodeDefs = result.page.nodeDefs || {};
			const mapped = (result.page.data || [])
				.map((run) => this.mapLibraryNodeRun(run, nodeDefs))
				.filter((r) => r != null);
			return {
				results: mapped,
				hasMore: result.page.hasMore,
				lastDoc: result.page.nextCursor, // opaque cursor string; null when done
			};
		},
		async fetchNodeRunsPage (lastDoc, pageSize: number, selectedFilter: string | null = null) {
			// Favorites always use the Postgres REST endpoint (favorites=true); the
			// filtered library (facets active) does too. Unfiltered ALL/TEAM keep the
			// Firestore path below.
			if (selectedFilter === 'FAVORITES' || this.isRestLibraryView) {
				return this.fetchFilterPage(lastDoc, pageSize, selectedFilter);
			}
			// Add one extra document to check if there are more results
			const queryLimit: number = pageSize + 1;

			// Use the passed filter (required parameter)
			const filterToUse = selectedFilter;

			// Check if this is library view (no specific runnit/node context)
			const isLibraryView = !this.runnitGalleryRunnit || !this.runnitGalleryNode;

			let nodeRunsRef = null;
			switch (filterToUse) {
				case 'ALL':
					// Show all generations from the user, not filtered by specific node/tool
					nodeRunsRef = db.collection('runnitNodeRuns')
						.where('state', 'in', this.FINAL_NODE_RUN_STATES)
						.where('deletedAt', '==', null)
						.where('userId', '==', this.user.id);

					// Filter out template clones in library view
					if (isLibraryView) {
						nodeRunsRef = nodeRunsRef.where('templateOriginalData', '==', null);
					}

					nodeRunsRef = nodeRunsRef.orderBy('initAt', 'desc').limit(queryLimit);
					break;
				case 'FEATURED':
					nodeRunsRef = db.collection('runnitNodeRuns')
						.where('isFeaturedNodeDef', '==', true)
						.where('nodeDefId', '==', this.runsGalleryNode.nodeDefId)
						.limit(queryLimit);
					break;
				case 'TEAM':
					// index created: runnitNodeRuns: delatedAt Ascending state Ascending teamId Ascending initAt Descending __name__ Descending
					nodeRunsRef = db.collection('runnitNodeRuns')
						.where('state', 'in', this.FINAL_NODE_RUN_STATES)
						.where('deletedAt', '==', null)
						.where('teamId', '==', this.team.id)
						.orderBy('initAt', 'desc')
						.limit(queryLimit);

					if (this.teamGenerationFilters) {
						if (this.teamGenerationFilters.userFilters) {
							if (this.teamGenerationFilters.userFilters.email) {
								// index created: runnitNodeRuns: deletedAt Ascending email Ascending state Ascending teamId Ascending initAt Descending __name__ Descending
								// index created: runnitNodeRuns: deletedAt Ascending email Ascending nodeDefId Ascending state Ascending teamId Ascending initAt Descending __name__ Descending
								// index created: runnitNodeRuns: deletedAt Ascending nodeDefId Ascending state Ascending userId Ascending initAt Descending __name__ Descending
								nodeRunsRef = nodeRunsRef.where('email', '==', this.teamGenerationFilters.userFilters.email);
							}
						}
						if (this.teamGenerationFilters.mediaFilters) {
							// TODO This is the result.type
						}
						if (this.teamGenerationFilters.dateRangeFilters) {
							// TODO we'll actually do this when we have postgres queries
							if (this.teamGenerationFilters.dateRangeFilters.startDate) {
								nodeRunsRef = nodeRunsRef.where('initAt', '>=', this.teamGenerationFilters.dateRangeFilters.startDate);
							}
							if (this.teamGenerationFilters.dateRangeFilters.endDate) {
								nodeRunsRef = nodeRunsRef.where('initAt', '<=', this.teamGenerationFilters.dateRangeFilters.endDate);
							}
						}
						if (this.teamGenerationFilters.toolFilters && this.teamGenerationFilters.toolFilters.length) {
							// TODO: since we are using an 'in' filter for the nodeDefId, we cannot have an in to select multiple tools
							// nodeRunsRef = nodeRunsRef.where('nodeDefId', 'in', Object.keys(this.teamGenerationFilters.toolFilters).slice(0, 10));

							// index created: runnitNodeRuns: delatedAt Ascending nodeDefId Ascendingstate Ascending teamId Ascending initAt Descending __name__ Descending
							nodeRunsRef = nodeRunsRef.where('nodeDefId', '==', (Object.values(this.teamGenerationFilters.toolFilters)[0] as any).id);
						}
					}
					break;
				case 'THIS_TOOL':
				default:
					nodeRunsRef = db.collection('runnitNodeRuns')
						.where('state', 'in', this.FINAL_NODE_RUN_STATES)
						.where('deletedAt', '==', null);

					if (this.runsGalleryRunnit && this.runsGalleryNode) {
						nodeRunsRef = nodeRunsRef
							.where('nodeId', '==', this.runsGalleryNode.id)
							.where('runnitId', '==', this.runsGalleryRunnit.id);
					} else {
						nodeRunsRef = nodeRunsRef
							.where('userId', '==', this.user.id);

						// Filter out template clones in library view
						if (isLibraryView) {
							nodeRunsRef = nodeRunsRef.where('templateOriginalData', '==', null);
						}
					}

					nodeRunsRef = nodeRunsRef.orderBy('initAt', 'desc').limit(queryLimit);
					break;
			}

			if (lastDoc) {
				nodeRunsRef = nodeRunsRef.startAfter(lastDoc);
			}

			const nodeRunsSnapshot = await nodeRunsRef.get();


			// Check if we have more results by seeing if we got more docs than requested page size
			const hasMore: boolean = nodeRunsSnapshot.docs.length > pageSize;

			// Only use the requested page size number of documents
			const docsToProcess = hasMore ? nodeRunsSnapshot.docs.slice(0, pageSize) : nodeRunsSnapshot.docs;

			const nodeRuns = await Promise.all(docsToProcess.map(async (doc) => {
				const nodeRun = doc.data() as RunnitNodeRun;
				const fetchedNodeDef = await this.fetchCachedNodeDefForNodeRun(nodeRun);

				// In FEATURED mode, each "featured" nodeRun can still have multiple results.
				// Only show the result(s) that are actually featured for the nodeDef.
				const resultsForThisFilter =
					filterToUse === 'FEATURED'
						? (nodeRun.results || []).filter((r: any) => r && r.isFeaturedNodeDef && !r.deletedAt)
						: nodeRun.results;

				return {
					...nodeRun,
					...(filterToUse === 'FEATURED' ? { results: resultsForThisFilter } : {}),
					...(fetchedNodeDef ? { nodeDef: fetchedNodeDef } : {}),
					get id () { return doc.id; }
				};
			}));

			return {
				results: nodeRuns,
				hasMore: hasMore,
				lastDoc: hasMore ? nodeRunsSnapshot.docs[pageSize - 1] : null
			};
		},
		handleCarouselStep ({ nodeRunResult, nodeRun }): void {
			if (!this.imageInfoCarouselConfig.dialogOpen) return;

			this.imageInfoCarouselConfig = {
				dialogOpen: !!(nodeRun && nodeRunResult),
				nodeRun,
				nodeRunResult,
			};
		},
		onFilterChange (filter: string) {
			this.$emit('on-filter-change', filter); // re-emit so RunnitLibraryPage can gate the toolbar

			// When switching to THIS_TOOL or ALL filter, re-add placeholders for existing realtime node runs
			// since they should only appear in THIS_TOOL/ALL filter, not FEATURED or TEAM
			if ((filter === 'THIS_TOOL' || filter === 'ALL' || !filter) && !this.realtimeNodeRunsLoading && this.realtimeNodeRuns.length) {
				this.$nextTick(() => {
					if (this.$refs.virtualScrollResults) {
						const existingPlaceholderIds: string[] = ((this.$refs.virtualScrollResults as any).results || [])
							.filter((r: any) => r && r.isPlaceholder)
							.map((r: any) => r.nodeRunId);
						this.realtimeNodeRuns.forEach(nodeRun => {
							let shouldShow = false;
							if (filter === 'ALL') {
								// Show all realtime node runs in ALL filter
								shouldShow = nodeRun.userId === this.user?.id;
							} else {
								// Show only node-specific runs in THIS_TOOL filter
								shouldShow = this.insideNode
									? nodeRun.nodeId === this.runsGalleryNode?.id
									: nodeRun.userId === this.user?.id;
							}
							if (shouldShow && !this.processedCompletedNodeRunIds.has(nodeRun.id) && !existingPlaceholderIds.includes(nodeRun.id)) {
								(this.$refs.virtualScrollResults as any).addPlaceholderRun(nodeRun);
							}
						});
					}
				});
			}
		},
		toggleOnboardingDismissed (nodeDefId: string) {
			this.$emit('toggle-onboarding-dismissed', nodeDefId);
		},
		async initializeFeaturedImagesCount () {
			if (!this._get(this.runsGalleryNode, 'nodeDefId')) {
				this.featuredImageFirstPageCount = 0;
				return;
			}

			const nodeRunsRef = db.collection('runnitNodeRuns')
				.where('isFeaturedNodeDef', '==', true)
				.where('nodeDefId', '==', this.runsGalleryNode.nodeDefId)
				.limit(this.featuredImagePageSize);
			const snapshot = await nodeRunsRef.get();
			this.featuredImageFirstPageCount = snapshot.docs.length;
		},
		hasPermissionForNodeRunList (newVal: RunnitNodeRun[]): boolean {
			// No data means no restriction
			if (!newVal || !newVal.length) {
				return true;
			}

			// Inside a specific node on a runnit: ensure the feed matches the node/runnit
			if (this.insideNode && this.runsGalleryNode && this.runsGalleryRunnit) {
				const allMatchNode = newVal.every(nr => (
					nr.nodeId === this.runsGalleryNode.id &&
					nr.runnitId === this.runsGalleryRunnit.id
				));
				return allMatchNode;
			}

			// Outside node context (e.g., personal feeds): require that list is owned by current user
			const isOwnedByCurrentUser = newVal[0].userId === this.user.id;
			return isOwnedByCurrentUser;
		},
		getNewNodeRuns (newVal: RunnitNodeRun[]): RunnitNodeRun[] {
			const newRealtimeNodeRuns: Record<string, RunnitNodeRun> = _mapKeys(newVal || [], 'id');
			const newIds = Object.keys(newRealtimeNodeRuns).filter((id: string) =>
				!this.previousRealtimeNodeRunIds.includes(id) && !this.processedCompletedNodeRunIds.has(id)
			);

			const filteredNewIds = newIds.filter(id => {
				const nodeRun = newRealtimeNodeRuns[id];
				const shouldInclude = !this.insideNode || !this.runsGalleryNode || nodeRun.nodeId === this.runsGalleryNode.id;

				return shouldInclude;
			});

			const result = filteredNewIds.map(id => newRealtimeNodeRuns[id]);

			return result;
		},
		getCompletedNodeRunIds (newVal: RunnitNodeRun[]): string[] {
			const newRealtimeNodeRuns: Record<string, RunnitNodeRun> = _mapKeys(newVal || [], 'id');
			const completedIds = this.previousRealtimeNodeRunIds.filter((id: string) =>
				!newRealtimeNodeRuns[id] && !this.processedCompletedNodeRunIds.has(id)
			);
			return completedIds;
		},
		handleRealtimeNodeRunsExpensive: _debounce(async function (newVal: RunnitNodeRun[]) {
			if (!this.hasPermissionForNodeRunList(newVal)) {
				return;
			}
			const idsOfCompletedNodeRuns = this.getCompletedNodeRunIds(newVal);
			await Promise.all(Array.from(this.pendingCompletedNodeRunIds || []).map(async (id: string) => {
				this.processedCompletedNodeRunIds.add(id);
				// Skip Firestore fetches and list inserts while the REST response owns
				// the filtered view, but keep the bookkeeping below this guard.
				if (!this.isRestLibraryView) {
					const nodeRunRef = db.collection('runnitNodeRuns').doc(id);
					const nodeRun: RunnitNodeRun = (await nodeRunRef.get()).data() as RunnitNodeRun;
					const nonDeletedResults = (nodeRun.results || []).filter((result: RunnitNodeRunResult) => !result.deletedAt);
					let fetchedNodeDef = null;
					if (!isResolvedNodeDef(nodeRun.nodeDef)) {
						try {
							const nodeDefRef = db.doc(`runnitNodeDefs/${nodeRun.nodeDefId}`);
							const nodeDef: RunnitNodeDef = (await nodeDefRef.get()).data() as RunnitNodeDef;
							fetchedNodeDef = { ...nodeDef, get id () { return nodeRun.nodeDefId } }
						} catch (err) {
							console.error('Failed to fetch the generating tool', err);
						}
					}
					if (
						nodeRun.state !== RUNNIT_NODE_RUN_STATE.DONE ||
						nonDeletedResults.length
					) {
						const updatedNodeRun = {
							...nodeRun,
							results: nonDeletedResults,
							get id () { return nodeRunRef.id; },
							...(fetchedNodeDef ? { nodeDef: fetchedNodeDef } : {}),
						};
						const existingIndex = this.moreNodeRuns.findIndex(existing => existing.id === id);
						if (existingIndex === -1) {
							this.moreNodeRuns = [
								updatedNodeRun,
								...this.moreNodeRuns,
							];
							if (this.$refs.virtualScrollResults) {
								(this.$refs.virtualScrollResults as any).addNewRun(updatedNodeRun);
							}
						} else {
							console.warn(`Attempted to add duplicate node run ${id} to moreNodeRuns, skipping`);
						}
					}
				}
				this.removeDraftLoadingTimer(id);
			}));

			// Clear the pending set after processing
			this.pendingCompletedNodeRunIds.clear();

			// Update error tracking map
			this.couldThrowGenerationError = (newVal || []).reduce((map, nodeRun: RunnitNodeRun) => {
				if (!Object.keys(map).includes(nodeRun.id)) {
					map[nodeRun.id] = true;
				}
				return map;
			}, this.couldThrowGenerationError);
		}, 500),
		onTeamGenerationsFiltersChange (filters: { mediaFilters: Record<string, boolean>, dateRangeFilters: Record<string, string>, toolFilters: Record<string, boolean>, userFilters: Record<string, string> }) {
			this.teamGenerationFilters = filters;
			this.$nextTick(() => {
				const vs: any = this.$refs.virtualScrollResults;
				if (vs && vs.selectedFilter === 'TEAM' && typeof vs.onFilterChange === 'function') {
					vs.onFilterChange('TEAM');
				}
			});
		},
	},
	components: {
		BaseGreyToggleButton,
		LoadingState,
		ImageInfo,
		RunnitImage,
		RunnitVideo,
		RunnitAsset3D,
		RunnitImageInfoCarouselDialog,
		UploadFullScreenDialog,
		ImageUpload,
		RunnitResult,
		ImageModeButton,
		SelectedColumnsButton,
		VirtualScrollResults,
	},
});
